import { useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  DollarSign,
  Receipt,
  AlertTriangle,
  Settings2,
  Film,
  Building2,
  Crown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { artistHubEnabled } from "@/services/firebase/artistHub";
import { useArtistFlags } from "@/hooks/useArtistFlags";

interface FinancialTab {
  key: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  /** Only where the v3 artist hub backend exists (sandbox until rollout). */
  artistHubOnly?: boolean;
  /** U2: only while RC artist_fans_enabled is on. */
  fansOnly?: boolean;
}

const TABS: FinancialTab[] = [
  { key: "revenue", label: "營收總覽", path: "/financial/revenue", icon: DollarSign },
  { key: "dramas", label: "劇集表現", path: "/financial/dramas", icon: Film },
  { key: "companies", label: "片商表現", path: "/financial/companies", icon: Building2 },
  { key: "transactions", label: "交易記錄", path: "/financial/transactions", icon: Receipt },
  { key: "fan-pass", label: "鐵粉月卡", path: "/financial/fan-pass", icon: Crown, artistHubOnly: true, fansOnly: true },
  { key: "failed-purchases", label: "失敗購買", path: "/financial/failed-purchases", icon: AlertTriangle },
  { key: "revenue-configs", label: "分帳設定", path: "/financial/revenue-configs", icon: Settings2, adminOnly: true },
];

export default function FinancialHubLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const { fansEnabled } = useArtistFlags();

  const visibleTabs = TABS.filter(
    (t) => (!t.adminOnly || isAdmin) && (!t.artistHubOnly || artistHubEnabled) && (!t.fansOnly || fansEnabled),
  );

  // 從 pathname 推斷 active tab：找最長 match 的 path
  const activeKey =
    visibleTabs
      .slice()
      .sort((a, b) => b.path.length - a.path.length)
      .find((t) => location.pathname.startsWith(t.path))?.key ?? "revenue";

  return (
    <div>
      {/* Tab nav — MainLayout 已提供 p-6，本元件不再加任何外部 padding */}
      <div className="border-b">
        <h1 className="text-xl font-semibold text-foreground mb-4">財務</h1>
        <div className="flex gap-1 -mb-px overflow-x-auto">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeKey === t.key;
            return (
              <button
                key={t.key}
                onClick={() => navigate(t.path)}
                className={cn(
                  "flex items-center gap-1.5 border-b-2 px-3 pb-3 pt-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                  isActive
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 子頁面內容：MainLayout 與 hub 已提供 padding，移除子頁 AdminPage 的全部 padding 避免疊加 */}
      <div className="[&>div]:!p-0 [&>div]:!pt-4">
        <Outlet />
      </div>
    </div>
  );
}
